import { gameRuntimeBridge } from "@/game/bridge/runtimeBridge.js";
import { computed, ref } from "vue";
import { defineStore } from "pinia";
import * as authApi from "@/api/authApi.js";
import * as gameApi from "@/api/gameApi.js";
import {
  useProgressStore
} from "@/stores/progressStore.js";
import { backendEnabled } from "@/services/backendMode.js";

const PLAY_STORAGE_KEY = "bricksbeater_active_play_v1";

function loadActivePlay() {
  try {
    const raw = sessionStorage.getItem(PLAY_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveActivePlay(play) {
  try {
    if (play) sessionStorage.setItem(PLAY_STORAGE_KEY, JSON.stringify(play));
    else sessionStorage.removeItem(PLAY_STORAGE_KEY);
  } catch {
    // SessionStorage failure should not block gameplay.
  }
}

export const useSessionStore = defineStore("session", () => {
  const progressStore =
    useProgressStore();

  const home = ref(null);
  const tiktokProfile = ref(null);
  const activePlay = ref(loadActivePlay());
  const historyLevels = ref([]);
  const loading = ref(false);
  const lastError = ref(null);
  const lastFinish = ref(null);
  let finishInFlight = Promise.resolve();
  let beginInFlight = null;

  const isRemote = computed(() => backendEnabled);

  async function refreshTikTokProfile() {
    if (!backendEnabled) return null;
    tiktokProfile.value = await authApi.getTikTokProfile();
    return tiktokProfile.value;
  }

  async function authorizeTikTokProfile() {
    if (!backendEnabled) return null;
    loading.value = true; lastError.value = null;
    try { tiktokProfile.value = await authApi.authorizeTikTokProfile(); return tiktokProfile.value; }
    catch (error) { lastError.value = error; throw error; }
    finally { loading.value = false; }
  }

  async function refreshHome({ syncRuntime = true } = {}) {
    if (!backendEnabled) return null;

    home.value =
      await gameApi.getGameHome();

    progressStore.applyHome(
      home.value
    );

    if (syncRuntime) {
      gameRuntimeBridge.setEnergy({
        current:
          home.value?.energy,
        max:
          home.value?.energy_max,
        nextRecoveryAt:
          home.value
            ?.energy_next_recovery_at
          ?? null
      });

      gameRuntimeBridge.setSkillInventory(
        home.value
      );
    }

    return home.value;
  }

  async function startCampaign() {
    if (!backendEnabled) return null;
    await finishInFlight;
    const play = await gameApi.startCampaign();
    activePlay.value = play;
    saveActivePlay(play);
    gameRuntimeBridge.setSkillInventory(play);
    await refreshHome();
    return play;
  }

  async function beginActivePlay() {
    if (!backendEnabled) {
      return {
        started: true,
        idempotent: true
      };
    }

    const playId =
      activePlay.value?.play_id;

    if (!playId) {
      throw new Error(
        "No prepared backend play is available."
      );
    }

    if (
      activePlay.value?.started === true
      && activePlay.value?.started_at
    ) {
      return {
        play_id: playId,
        started: true,
        started_at:
          activePlay.value.started_at,
        energy:
          home.value?.energy ?? null,
        energy_max:
          home.value?.energy_max ?? null,
        idempotent: true
      };
    }

    if (beginInFlight) {
      return beginInFlight;
    }

    beginInFlight = gameApi
      .beginPlay(playId)
      .then(response => {
        activePlay.value = {
          ...activePlay.value,
          started: true,
          started_at:
            response?.started_at ?? null
        };

        saveActivePlay(
          activePlay.value
        );

        if (
          home.value
          && response?.energy != null
        ) {
          home.value = {
            ...home.value,
            energy:
              response.energy,
            energy_max:
              response.energy_max
              ?? home.value.energy_max
          };
        }

        if (
          response?.energy != null
        ) {
          gameRuntimeBridge.setEnergy({
            current:
              response.energy,
            max:
              response.energy_max
          });
        }

        return response;
      })
      .catch(error => {
        lastError.value = error;
        throw error;
      })
      .finally(() => {
        beginInFlight = null;
      });

    return beginInFlight;
  }


  async function loadHistoryLevels() {
    if (!backendEnabled) return [];
    historyLevels.value = await gameApi.getHistoryLevels();
    return historyLevels.value;
  }

  async function startHistory(levelNo) {
    if (!backendEnabled) return null;
    await finishInFlight;
    const play = await gameApi.startHistoryLevel(levelNo);
    activePlay.value = play;
    saveActivePlay(play);
    gameRuntimeBridge.setSkillInventory(play);
    await refreshHome();
    return play;
  }

  async function useActivePlaySkill(
    skillType
  ) {
    if (
      !backendEnabled
      || !activePlay.value?.play_id
    ) {
      return null;
    }

    const response =
      await gameApi.usePlaySkill(
        activePlay.value.play_id,
        skillType
      );

    gameRuntimeBridge
      .setSkillInventory(
        response
      );

    activePlay.value = {
      ...activePlay.value,
      split_ball_skill_count:
        response
          ?.split_ball_skill_count
        ?? activePlay.value
          ?.split_ball_skill_count,
      triple_launch_skill_count:
        response
          ?.triple_launch_skill_count
        ?? activePlay.value
          ?.triple_launch_skill_count
    };

    saveActivePlay(
      activePlay.value
    );

    if (home.value) {
      home.value = {
        ...home.value,
        split_ball_skill_count:
          response
            ?.split_ball_skill_count
          ?? home.value
            ?.split_ball_skill_count,
        triple_launch_skill_count:
          response
            ?.triple_launch_skill_count
          ?? home.value
            ?.triple_launch_skill_count
      };
    }

    return response;
  }


  async function finishActivePlay({
    result,
    score = 0,
    fragmentCollected = false
  } = {}) {
    if (
      !backendEnabled
      || !activePlay.value?.play_id
    ) {
      return null;
    }

    const playId = activePlay.value.play_id;

    finishInFlight = gameApi.finishPlay(
      playId,
      {
        result,
        score,
        fragmentCollected
      }
    )
      .then(async response => {
        lastFinish.value = response;

        gameRuntimeBridge
          .setSkillInventory(
            response
          );

        activePlay.value = null;
        saveActivePlay(null);

        try {
          // The finish response has already synchronized the runtime inventory.
          // Refresh only the Vue-side home snapshot so the same counts are not
          // interpreted as a second reward with a delta of zero.
          await refreshHome({ syncRuntime: false });
        } catch {
          // Finish succeeded. Home refresh may retry later.
        }

        return response;
      })
      .catch(error => {
        lastError.value = error;
        throw error;
      });

    return finishInFlight;
  }

  async function logout() {
    if (backendEnabled) await authApi.logout();
    activePlay.value = null;
    home.value = null;
    tiktokProfile.value = null;
    saveActivePlay(null);
  }

  return {
    backendEnabled: isRemote,
    home,
    tiktokProfile,
    activePlay,
    historyLevels,
    loading,
    lastError,
    lastFinish,
    refreshTikTokProfile,
    authorizeTikTokProfile,
    refreshHome,
    startCampaign,
    beginActivePlay,
    loadHistoryLevels,
    startHistory,
    useActivePlaySkill,
    finishActivePlay,
    logout
  };
});
